import { useReactFlow, type Node } from '@xyflow/react'
import { useRef, useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNodeZIndex } from '../hooks/useNodeZIndex'
import { useHasChildren } from '../../hooks/useHasChildren'
import { useZoomState } from '../../hooks/useZoomState'
import { useReadOnlyContext } from '../../../../context/readOnly'
import { useFeatureAccessContext } from '../../../../context/featureAccess'
import { useNodeActions } from '../../../../context/nodeActions'
import { useAINodeState } from '../../hooks/useAINodeState'
import { useAutoSaveField } from '../hooks/useAutoSaveField'
import { hasMinimumQualityScore } from '../../utils/nodeContentUtils'
import { useActiveChatContext } from '../../../../context/activeChat'
import { CardFooterBar } from './components/CardFooterBar'
import { NodeChat } from '../NodeChat/NodeChat'
import type { ReactFlowNodeData } from '../../../../types/mindmap-components'

// Sub-modules
import { getCategoryStyle, getChildNodeInfo } from './nodeHelpers'
import { useNodeLabels } from './hooks/useNodeLabels'
import { useCollapsedPreview } from './hooks/useCollapsedPreview'
import { NodeHeader } from './components/NodeHeader'
import { NodeContent } from './components/NodeContent'
import { NodeHandles } from './components/NodeHandles'
import { CardActions } from './components/CardActions'
import { CollapsedView } from './components/CollapsedView'
import { NodeLabels } from './components/NodeLabels'
import { NodePreviewOverlay } from './components/PreviewOverlay'
import { NodeWrapper, StyledNodeContainer, MainSection } from './styles/BaseNodeStyles'

// Re-exports for external consumers
export type { BaseNodeProps } from './BaseNodeTypes'

// Import type for this file's usage
import type { BaseNodeProps } from './BaseNodeTypes'

const BaseNodeInner = ({
  nodeData,
  id,
  icon: CategoryIconComponent,
  categoryLabel,
  categorySubheading,
  categoryDescription,
  renderNodeContent,
  borderLayers,
  hideDefaultContent = false,
  renderHeaderActions,
  hideSourceHandle = false,
  onSourceHandleClick,
  borderColorOverride,
  hasDirectTag = false,
  renderLeftHandle,
  renderAboveNode,
  onChatReady,
  dockActions,
  prototypeActions,
}: BaseNodeProps) => {
  // Get callbacks from NodeActionsContext
  const {
    handleDeleteNode,
    handleUpdateNodeLabel,
    handleCollapseNode,
    handleAddChild,
    autoEditNodeId,
    clearAutoEdit,
  } = useNodeActions()

  const navigate = useNavigate()

  // Get AI state for THIS specific node via optimized subscription
  const {
    isGenerating: isNodeGenerating,
    isReady: isNodeReady,
    hasSuggestions,
    suggestionCount,
  } = useAINodeState(id)

  // Get React Flow instance for node lookup
  const { setCenter, getNode } = useReactFlow()

  // Feature toggles
  const { isFeatureEnabled } = useFeatureAccessContext()
  const qualityGateEnabled = isFeatureEnabled('node_quality_gate')
  const labelsEnabled = isFeatureEnabled('labels')

  // Track when content criteria for AI generation is first met
  const [criteriaJustMet, setCriteriaJustMet] = useState(false)
  const prevCanGenerateRef = useRef<boolean | null>(null)

  // Check if this node meets content criteria for AI generation
  const currentNode = getNode(id) as Node<ReactFlowNodeData> | undefined
  const canGenerate = currentNode ? hasMinimumQualityScore(currentNode, qualityGateEnabled) : false

  // Detect transition from false to true (criteria just met)
  useEffect(() => {
    if (prevCanGenerateRef.current === false && canGenerate === true) {
      setCriteriaJustMet(true)
      const timer = setTimeout(() => setCriteriaJustMet(false), 700)
      return () => clearTimeout(timer)
    }
    prevCanGenerateRef.current = canGenerate
    return undefined
  }, [canGenerate])

  // Combined pulse condition: either AI suggestions ready OR criteria just met
  const shouldPulse = isNodeReady || criteriaJustMet

  // Check if this node's chat panel is currently open
  const { activeChatNodeId } = useActiveChatContext()
  const isChatOpen = activeChatNodeId === id

  // Extract UI data from node data
  const { mindmapId, labels } = nodeData

  // Check if this node has children
  const hasChildren = useHasChildren(id)

  // Get zoom state
  const { isCollapsed, zoomScale, zoomScaleRaw } = useZoomState()

  // Read-only mode
  const { isReadOnly } = useReadOnlyContext()

  // Ref for the node container element
  const nodeContainerRef = useRef<HTMLDivElement>(null)

  const isRootNode = id === '1'

  // Text editing functionality
  const nodeTextField = useAutoSaveField(
    nodeData.label,
    (value) => handleUpdateNodeLabel(id, value),
    { autoResize: true, minHeight: 26, nodeId: id, autoEditNodeId, clearAutoEdit }
  )

  // Use centralized z-index management hook
  const bringNodeToFront = useNodeZIndex(id)

  // Update node zIndex when text field is focused/blurred
  useEffect(() => {
    if (nodeTextField.isFocused) {
      bringNodeToFront()
    }
  }, [nodeTextField.isFocused, bringNodeToFront])

  // Get category style for child components with fallback
  const nodeCategoryStyle = getCategoryStyle(nodeData.category)

  // Get child node info for the source handle (right-side button)
  const childNodeInfo = getChildNodeInfo(nodeData.category, nodeData.phase)

  // Label management
  const {
    labelContext,
    handleSelectLabel,
    handleCreateLabel,
    handleRemoveLabel,
    handleDeleteLabel,
  } = useNodeLabels({ nodeId: id, mindmapId, labels })

  // Trigger textarea resize when node expands from collapsed state
  useEffect(() => {
    if (!isCollapsed) {
      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          nodeTextField.triggerResize()
        })
      })
      return () => cancelAnimationFrame(rafId)
    }
    return undefined
  }, [isCollapsed, nodeTextField])

  // Collapsed hover preview
  const {
    isHovered,
    mousePosition,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseMove,
  } = useCollapsedPreview({ isCollapsed })

  const gradientColor =
    (nodeCategoryStyle as { gradientColor?: string }).gradientColor ?? nodeCategoryStyle.iconColor

  const collapsedIconColor = borderColorOverride ?? nodeCategoryStyle.iconColor

  const handleNavigateToReport = useCallback(() => {
    void navigate(`/mindmap/${nodeData.mindmapId}/report?nodeFilter=${id}`)
  }, [id, navigate, nodeData.mindmapId])

  const hasDock = dockActions !== undefined && dockActions.length > 0

  // Shared handles JSX — used in both content-visible and content-hidden modes
  // When dock is active, NodeHandles only renders invisible target + left handle
  const nodeHandles = (
    <NodeHandles
      hideSourceHandle={hideSourceHandle}
      hideVisualHandle={hasDock}
      isReadOnly={isReadOnly}
      childNodeInfo={childNodeInfo}
      shouldPulse={shouldPulse}
      isNodeGenerating={isNodeGenerating}
      hasSuggestions={hasSuggestions}
      suggestionCount={suggestionCount}
      nodeId={id}
      nodeCategory={nodeData.category}
      onSourceHandleClick={onSourceHandleClick}
      handleAddChild={handleAddChild}
      renderLeftHandle={renderLeftHandle}
    />
  )

  // Inline card actions — rendered inside the card body (replaces the old side-panel ActionDock)
  const cardActions =
    hasDock && !isCollapsed && !isReadOnly ? <CardActions actions={dockActions} /> : null

  return (
    <>
      <NodeWrapper $zoomScale={zoomScale} $zoomScaleRaw={zoomScaleRaw}>
        {/* Optional content above the node - only shown when not collapsed */}
        {!isCollapsed && renderAboveNode && renderAboveNode()}
        <StyledNodeContainer
          ref={nodeContainerRef}
          data-node-id={id}
          $category={nodeData.category}
          $isFocused={nodeTextField.isFocused}
          $borderLayers={borderLayers}
          $borderColorOverride={borderColorOverride}
          $hasDirectTag={hasDirectTag}
          $isCollapsed={isCollapsed}
          onClickCapture={bringNodeToFront}
          onClick={(e) => {
            e.stopPropagation()
            if (isCollapsed) {
              const node = getNode(id)
              if (node) {
                const nodeWidth = node.measured?.width ?? 400
                const nodeHeight = node.measured?.height ?? 80
                void setCenter(node.position.x + nodeWidth / 2, node.position.y + nodeHeight / 2, {
                  zoom: 0.8,
                  duration: 600,
                })
              }
            }
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseDownCapture={handleMouseDown}
          onMouseMove={handleMouseMove}
        >
          {/* Main section — lighter inset */}
          {!isCollapsed && (
            <MainSection $gradientColor={gradientColor}>
              <NodeHeader
                icon={CategoryIconComponent}
                iconColor={nodeCategoryStyle.iconColor}
                categoryLabel={categoryLabel}
                categoryDescription={categoryDescription}
                categorySubheading={categorySubheading}
                nodeData={nodeData}
                nodeId={id}
                isRootNode={isRootNode}
                hasChildren={hasChildren}
                isReadOnly={isReadOnly}
                onDeleteNode={handleDeleteNode}
                onNavigateToReport={handleNavigateToReport}
                onCollapse={
                  hasChildren && nodeData.category !== 'venture'
                    ? () => handleCollapseNode(id)
                    : undefined
                }
                renderHeaderActions={renderHeaderActions}
              />
              {!hideDefaultContent && (
                <NodeContent
                  nodeTextField={nodeTextField}
                  nodeData={nodeData}
                  isReadOnly={isReadOnly}
                >
                  {nodeHandles}
                </NodeContent>
              )}
              {hideDefaultContent && nodeHandles}
              {renderNodeContent && renderNodeContent(nodeTextField.value)}
            </MainSection>
          )}

          {/* Collapsed view - circular icon with tags */}
          {isCollapsed && (
            <CollapsedView
              icon={CategoryIconComponent}
              iconColor={collapsedIconColor}
              labels={labelsEnabled ? labels : undefined}
              hideSourceHandle={hideSourceHandle}
            />
          )}

          {/* Inline CTA buttons (branch, validate groups) */}
          {cardActions}

          {/* Node chat — hidden by default, opens when Generate Suggestions is triggered */}
          {!isCollapsed && !isReadOnly && (
            <NodeChat nodeId={id} isOpen={isChatOpen} onReady={onChatReady} />
          )}

          {/* Card footer: prototype dropdown + sources toggle */}
          {!isCollapsed && (
            <CardFooterBar
              prototypeActions={isReadOnly ? undefined : prototypeActions}
              ragSources={
                nodeData.ragSources as { fileName: string; excerpt: string }[] | undefined
              }
            />
          )}
        </StyledNodeContainer>

        {/* External Labels - positioned outside the card at bottom left (hidden when collapsed) */}
        {labelsEnabled && nodeData.category !== 'venture' && !isCollapsed && (
          <NodeLabels
            labels={labels}
            nodeId={id}
            mindmapId={mindmapId}
            isReadOnly={isReadOnly}
            labelContext={labelContext}
            onSelectLabel={handleSelectLabel}
            onCreateLabel={handleCreateLabel}
            onRemoveLabel={handleRemoveLabel}
            onDeleteLabel={handleDeleteLabel}
          />
        )}
      </NodeWrapper>

      {/* Preview overlay - shown when hovering over collapsed node */}
      {isCollapsed && isHovered && (
        <NodePreviewOverlay
          mousePosition={mousePosition}
          icon={CategoryIconComponent}
          iconColor={nodeCategoryStyle.iconColor}
          gradientColor={gradientColor}
          borderColorOverride={borderColorOverride}
          categoryLabel={categoryLabel}
          categorySubheading={categorySubheading}
          hideDefaultContent={hideDefaultContent}
          nodeText={nodeTextField.value || nodeData.label}
          labels={labels}
          renderNodeContent={renderNodeContent}
          currentText={nodeTextField.value}
        />
      )}
    </>
  )
}

// Export directly - React Flow handles node rendering optimization
export const BaseNode = BaseNodeInner
